import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sum } from "lodash";
import update from "react-addons-update";
import { FormattedMessage } from "react-intl";
import { openSnackbar } from "store/slices/snackbar";
import { dispatch } from "../index";
import { setConflictCartAlbum, startLoadingAlbum } from "./ecommerce";

const initialState = {
  products: [],
  total: 0,
  albumId: null,
  address: null,
  email: null,
};

const slice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = action.payload;
    },

    addProductSuccess(state, action) {
      const obj = {
        type: action.payload.type,
        key: action.payload.key,
        package_id: null,
        package: null,
        photo: {
          key: action.payload.key,
          name: action.payload.name,
        },
        quantity: 1,
        contestant: action.payload.contestant,
        selfieImg: action.payload.selfieImg,
        pack_selfie_id: action.payload.packSelfieId,
        detailedData: action.payload.detailedData,
      };
      const index = state.products.findIndex((p) => p.key === obj.key);
      if (index === -1) {
        state.products = update(state.products, {
          $push: [obj],
        });
      } else {
        throw new Error("Product already in cart");
      }
    },

    updateProductFormat(state, action) {
      const { productKey, selectedPackage } = action.payload;
      const index = state.products.findIndex((p) => p.key === productKey);
      if (index !== -1 && selectedPackage) {
        state.products = update(state.products, {
          [index]: {
            package_id: { $set: selectedPackage.id },
            package: { $set: selectedPackage },
            unit_price: { $set: selectedPackage.unit_price },
            quantity: {
              $apply: (x) => {
                if (selectedPackage.is_download) {
                  return 1;
                }
                return x;
              },
            },
          },
        });
      }
    },

    updateProductQuantity(state, action) {
      const { productKey, newQuantity } = action.payload;
      const index = state.products.findIndex((p) => p.key === productKey);
      if (index !== -1) {
        state.products = update(state.products, {
          [index]: {
            quantity: { $set: newQuantity },
          },
        });
      }
    },

    removeProductSuccess(state, action) {
      state.products = state.products.filter(
        (p) => p.key !== action.payload.key
      );
    },

    clearCart(state) {
      state.products = [];
      state.albumId = null;
    },

    clearAddressAndEmail(state) {
      state.address = initialState.address;
      state.email = initialState.email;
    },

    selectAlbumId(state, action) {
      state.albumId = action.payload;
    },

    // SET STEP
    setStepSuccess(state, action) {
      state.step = action.payload;
    },

    // SET NEXT STEP
    setNextStepSuccess(state) {
      state.step += 1;
    },

    // SET BACK STEP
    setBackStepSuccess(state) {
      state.step -= 1;
    },

    setAddress(state, action) {
      state.address = action.payload;
    },

    setEmail(state, action) {
      state.email = action.payload;
    },

    unValidateFormat(state, action) {
      const productKey = action.payload;
      const index = state.products.findIndex((p) => p.key === productKey);
      state.products = update(state.products, {
        [index]: {
          package_id: { $set: null },
          package: { $set: null },
          quantity: { $set: 1 },
          unit_price: { $set: 0 },
        },
      });
      state.total = 0;
      state.formats = [];
    },
  },
});

// Reducer
export default slice.reducer;

export const hasProducts = createSelector(
  (state) => state.cart.products,
  (products) => {
    return products && products.length > 0;
  }
);

export const isProductInCart = createSelector(
  [(state) => state.cart.products, (state, photoKey) => photoKey],
  // Output selector gets (`items, category)` as args
  (products, photoKey) => products.find((product) => product.key === photoKey)
);

export const packAthletesSelector = createSelector(
  (state) => state.cart.products,
  (products) => {
    return products.filter((p) => p.type === "athlete");
  }
);

export const packSelfiesSelector = createSelector(
  (state) => state.cart.products,
  (products) => {
    return products.filter((p) => p.type === "selfie");
  }
);

export const totalQuantitySelector = createSelector(
  (state) => state.cart.products,
  (products) => {
    return sum(products.map((p) => p.quantity));
  }
);

export function addProduct(product) {
  return async (dispatch, getState) => {
    try {
      window.gtag("event", "add_to_cart", {
        currency: "EUR",
        item: product,
      });
      const albumId = getState().cart.albumId || product.photograph_album_id;
      if (product.photograph_album_id !== albumId) {
        dispatch(
          openSnackbar({
            open: true,
            message: (
              <FormattedMessage id="public.conflictDialog.description1" />
            ),
            variant: "alert",
            alert: {
              color: "error",
            },
            close: false,
          })
        );
        dispatch(setConflictCartAlbum(true, product));
      } else {
        dispatch(slice.actions.selectAlbumId(product.photograph_album_id));
        dispatch(slice.actions.addProductSuccess({ ...product }));
        dispatch(
          openSnackbar({
            open: true,
            message: (
              <FormattedMessage
                id="public.checkout.added"
                values={{ productName: product.name }}
              />
            ),
            variant: "alert",
            alert: {
              color: "success",
            },
            close: false,
          })
        );
      }
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addPackAthleteProduct(contestant, albumId) {
  return async (dispatch, getState) => {
    try {
      const product = {
        key: `athlete${contestant.bib}`,
        type: "athlete",
        name: "Pack participant",
        quantity: 1,
        contestant: contestant,
        photograph_album_id: albumId,
      };
      const albumCartId = getState().cart.albumId;
      if (albumCartId !== albumId) {
        dispatch(
          openSnackbar({
            open: true,
            message: (
              <FormattedMessage id="public.conflictDialog.description1" />
            ),
            variant: "alert",
            alert: {
              color: "error",
            },
            close: false,
          })
        );
        dispatch(setConflictCartAlbum(true, product));
      } else {
        dispatch(slice.actions.addProductSuccess(product));
        dispatch(slice.actions.selectAlbumId(albumId));
      }
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removePackAthleteProduct(contestant) {
  return async (dispatch, _) => {
    try {
      dispatch(
        slice.actions.removeProductSuccess({
          key: `athlete${contestant.bib}`,
        })
      );
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addPackSelfieProduct(packSelfieId, albumId, selfieImg, data) {
  console.log("addPackSelfieProduct", {
    packSelfieId,
    albumId,
    selfieImg,
  });
  return async (dispatch, getState) => {
    try {
      const product = {
        key: `selfie${packSelfieId}`,
        type: "selfie",
        name: `Pack Selfie #${packSelfieId}`,
        quantity: 1,
        photograph_album_id: albumId,
        package_id: packSelfieId,
        packSelfieId,
        detailedData: {
          ...data,
          packSelfieId: packSelfieId,
        },
        selfieImg: selfieImg,
      };
      const albumCartId = getState().cart.albumId;
      console.log(`albumCartId: ${albumCartId}, albumId: ${albumId}`);
      if (albumCartId !== albumId) {
        dispatch(
          openSnackbar({
            open: true,
            message: (
              <FormattedMessage id="public.conflictDialog.description1" />
            ),
            variant: "alert",
            alert: {
              color: "error",
            },
            close: false,
          })
        );
        dispatch(setConflictCartAlbum(true, product));
      } else {
        dispatch(slice.actions.addProductSuccess(product));
        dispatch(slice.actions.selectAlbumId(albumId));
      }
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removePackSelfieProduct(packSelfieId) {
  return async (dispatch, _) => {
    try {
      dispatch(
        slice.actions.removeProductSuccess({
          key: `selfie${packSelfieId}`,
        })
      );
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateProductFormat(productKey, package_id) {
  return async (_, getState) => {
    try {
      const checkoutAlbum = getState().ecommerce.checkoutAlbum;
      const selectedPackage = checkoutAlbum.album_packages.find(
        (p) => p.id === package_id
      );
      if (!selectedPackage) {
        dispatch(
          openSnackbar({
            open: true,
            message:
              "Impossible de choisir ce format, veuillez réessayer après avoir raffraichit la page",
            variant: "alert",
            alert: {
              color: "error",
            },
            close: false,
          })
        );
      } else {
        dispatch(
          slice.actions.updateProductFormat({
            productKey,
            selectedPackage,
          })
        );
      }
    } catch (error) {
      console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateProductQuantity(productKey, newQuantity) {
  return async () => {
    try {
      dispatch(
        slice.actions.updateProductQuantity({
          productKey,
          newQuantity,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function removeProduct(key, format) {
  return async () => {
    try {
      window.gtag("event", "remove_from_cart", {
        currency: "EUR",
        key,
      });
      dispatch(
        slice.actions.removeProductSuccess({
          key,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function clearCart() {
  return async () => {
    dispatch(slice.actions.clearCart());
  };
}

export function endOrder() {
  return () => {
    dispatch(slice.actions.clearCart());
    dispatch(slice.actions.clearAddressAndEmail());
  };
}

export function addAddressAndEmail(values) {
  return () => {
    dispatch(slice.actions.setAddress(values));
    dispatch(slice.actions.setEmail(values.email1));
  };
}

export function navigateToCartAlbum(navigate) {
  return async (dispatch, getState) => {
    dispatch(startLoadingAlbum());
    navigate(`/albums/${getState().cart.albumId}`, { replace: true });
  };
}

export function verifyPackageProducts() {
  return async (dispatch, getState) => {
    const checkoutAlbum = getState().ecommerce.checkoutAlbum;
    const packageIds = checkoutAlbum.album_packages.map((f) => f.id);
    const photos = getState().cart.products;
    photos.forEach((p) => {
      if (!packageIds.includes(p.package_id)) {
        dispatch(slice.actions.unValidateFormat(p.key));
      }
    });
  };
}
